import React, { useEffect, useState } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import dayjs from 'dayjs';

interface CountdownTimerProps {
  targetDate: string;
  /** Shown once the target date has passed */
  overdueLabel?: string;
  /** Compact renders inline digits only (for cards); false renders the full boxed unit layout */
  compact?: boolean;
}

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

const getRemaining = (targetDate: string): Remaining => {
  const totalMs = dayjs(targetDate).diff(dayjs());
  const clamped = Math.max(0, totalMs);
  const days = Math.floor(clamped / (1000 * 60 * 60 * 24));
  const hours = Math.floor((clamped / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((clamped / (1000 * 60)) % 60);
  const seconds = Math.floor((clamped / 1000) % 60);
  return { days, hours, minutes, seconds, totalMs };
};

/** green > amber > red as the deadline approaches; red pulses once overdue */
const getUrgencyColor = (remaining: Remaining): string => {
  if (remaining.totalMs <= 0) return '#e53935';
  const hoursLeft = remaining.totalMs / (1000 * 60 * 60);
  if (hoursLeft > 48) return '#2e7d32';
  if (hoursLeft > 12) return '#f9a825';
  return '#e53935';
};

/**
 * Live countdown, ticking every second. Used on sprint dashboard cards to
 * show time left in the active sprint (or "Overdue" once the end date passes).
 */
const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetDate,
  overdueLabel = 'Overdue',
  compact = false,
}) => {
  const [remaining, setRemaining] = useState<Remaining>(() => getRemaining(targetDate));

  useEffect(() => {
    setRemaining(getRemaining(targetDate));
    const interval = setInterval(() => {
      setRemaining(getRemaining(targetDate));
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const color = getUrgencyColor(remaining);
  const isOverdue = remaining.totalMs <= 0;

  if (isOverdue) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.5,
          borderRadius: 2,
          bgcolor: alpha(color, 0.12),
          animation: 'countdown-pulse 1.6s ease-in-out infinite',
          '@keyframes countdown-pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.55 },
          },
        }}
      >
        <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
          {overdueLabel}
        </Typography>
      </Box>
    );
  }

  if (compact) {
    return (
      <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
        {remaining.days}d {remaining.hours}h {remaining.minutes}m {remaining.seconds}s
      </Typography>
    );
  }

  const units: Array<{ label: string; value: number }> = [
    { label: 'Days', value: remaining.days },
    { label: 'Hrs', value: remaining.hours },
    { label: 'Min', value: remaining.minutes },
    { label: 'Sec', value: remaining.seconds },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {units.map((unit) => (
        <Box
          key={unit.label}
          sx={{
            minWidth: 44,
            textAlign: 'center',
            borderRadius: 1.5,
            px: 1,
            py: 0.5,
            bgcolor: alpha(color, 0.1),
            border: '1px solid',
            borderColor: alpha(color, 0.25),
          }}
        >
          <Typography variant="subtitle2" sx={{ color, fontWeight: 800, lineHeight: 1.1 }}>
            {String(unit.value).padStart(2, '0')}
          </Typography>
          <Typography variant="caption" sx={{ color: alpha(color, 0.8), fontSize: 10 }}>
            {unit.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default CountdownTimer;
